export interface MinimapEnemy {
  readonly x: number;
  readonly z: number;
}

export interface MinimapOptions {
  /** World-space radius (in units) from the ship to the edge of the dial. */
  range?: number;
  /** Diameter of the dial in CSS pixels. */
  size?: number;
}

const DEFAULT_RANGE = 200;
const DEFAULT_SIZE = 150;

/**
 * Lightweight 2D canvas minimap overlay.
 *
 * The dial is north-up relative to the ship: it rotates so the ship's heading
 * always points to the top of the screen. Purely a DOM/canvas overlay — it never
 * touches the Three.js scene graph, so it stays cheap per frame.
 */
export class Minimap {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly size: number;
  private range: number;

  constructor(options: MinimapOptions = {}) {
    this.size = options.size ?? DEFAULT_SIZE;
    this.range = Math.max(1, options.range ?? DEFAULT_RANGE);

    const dpr =
      typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.round(this.size * dpr);
    this.canvas.height = Math.round(this.size * dpr);
    this.canvas.setAttribute('aria-hidden', 'true');
    Object.assign(this.canvas.style, {
      position: 'fixed',
      top: 'max(14px, env(safe-area-inset-top))',
      right: 'max(14px, env(safe-area-inset-right))',
      width: `${this.size}px`,
      height: `${this.size}px`,
      // Keep the drawing area exactly `size` so the backing store maps 1:1.
      boxSizing: 'content-box',
      borderRadius: '50%',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      pointerEvents: 'none',
      zIndex: '5',
      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
    });

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Minimap requires a 2D canvas context');
    this.ctx = ctx;
    this.ctx.scale(dpr, dpr);

    document.body.appendChild(this.canvas);
  }

  /** Set how many world units are shown from the ship to the dial edge. */
  setRange(range: number): void {
    this.range = Math.max(1, range);
  }

  /**
   * Redraw the dial.
   *
   * `shipHeading` and `windDir` use the world convention where angle 0 points
   * along +X and increasing angles rotate toward -Z (bow +X at yaw 0).
   */
  update(
    shipX: number,
    shipZ: number,
    shipHeading: number,
    windDir: number,
    enemies: Iterable<MinimapEnemy> = [],
  ): void {
    const ctx = this.ctx;
    const size = this.size;
    const center = size / 2;
    const radius = center;
    const scale = radius / this.range;

    ctx.clearRect(0, 0, size, size);

    // Ocean background.
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = '#0b2a3f';
    ctx.fill();

    // Range rings for a sense of distance.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    for (const factor of [0.5, 1]) {
      ctx.beginPath();
      ctx.arc(center, center, radius * factor, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Enemies as red dots, rotated into the ship-relative frame.
    ctx.fillStyle = '#ff5d5d';
    for (const enemy of enemies) {
      const dx = enemy.x - shipX;
      const dz = enemy.z - shipZ;
      const distance = Math.hypot(dx, dz);
      if (distance > this.range) continue;
      const rel = Math.atan2(-dz, dx) - shipHeading;
      const x = center - Math.sin(rel) * distance * scale;
      const y = center - Math.cos(rel) * distance * scale;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Wind direction as a cyan arrow from the center.
    const windRel = windDir - shipHeading;
    const dirX = -Math.sin(windRel);
    const dirY = -Math.cos(windRel);
    const tail = 9;
    const length = radius * 0.42;
    const tailX = center + dirX * tail;
    const tailY = center + dirY * tail;
    const tipX = center + dirX * length;
    const tipY = center + dirY * length;

    ctx.strokeStyle = 'rgba(90, 220, 255, 0.95)';
    ctx.fillStyle = 'rgba(90, 220, 255, 0.95)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    const head = 7;
    const perpX = -dirY;
    const perpY = dirX;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(
      tipX - dirX * head + perpX * head * 0.5,
      tipY - dirY * head + perpY * head * 0.5,
    );
    ctx.lineTo(
      tipX - dirX * head - perpX * head * 0.5,
      tipY - dirY * head - perpY * head * 0.5,
    );
    ctx.closePath();
    ctx.fill();

    // Player ship: white triangle centered on the dial, always pointing up.
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(10, 30, 45, 0.9)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(center, center - 7);
    ctx.lineTo(center - 5, center + 6);
    ctx.lineTo(center, center + 3);
    ctx.lineTo(center + 5, center + 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  dispose(): void {
    this.canvas.remove();
  }
}
